import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  RELATION_CHILD_OF,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  CompoundEntityRef,
  Entity,
  RELATION_MEMBER_OF,
} from '@backstage/catalog-model';
import {
  CodeSnippet,
  Table,
  TableColumn,
  TableProps,
  WarningPanel,
} from '@backstage/core-components';
import {
  getEntityRelations,
  humanizeEntityRef,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { VisuallyHidden } from '@backstage/ui';
import { withStyles } from '@material-ui/core/styles';
import Edit from '@material-ui/icons/Edit';
import OpenInNew from '@material-ui/icons/OpenInNew';
import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';
import { capitalize } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { columnFactories } from './columns';
import { CatalogTableRow } from './types';
import pluralize from 'pluralize';
import { entityUtil } from '@appfolio/backstage-plugin-catalog-common';
import {
  ANNOTATION_GITHUB_USER_LOGIN,
  ANNOTATION_SLACK_NAME,
  ANNOTATION_USER_LOCATION,
  ANNOTATION_USER_ROLE,
  ANNOTATION_USER_TITLE,
} from '@appfolio/backstage-plugin-catalog-common';

export interface CatalogTableProps {
  columns?: TableColumn<CatalogTableRow>[];
  actions?: TableProps<CatalogTableRow>['actions'];
  tableOptions?: TableProps<CatalogTableRow>['options'];
  emptyContent?: ReactNode;
  subtitle?: string;
}

const YellowStar = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Star);

const refCompare = (a: Entity, b: Entity) => {
  const toRef = (entity: Entity) =>
    entity.metadata.title ??
    humanizeEntityRef(entity, {
      defaultKind: 'Component',
    });
  return toRef(a).localeCompare(toRef(b));
};

export const CatalogTable = (props: CatalogTableProps) => {
  const { columns, actions, tableOptions, subtitle, emptyContent } = props;
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { loading, error, entities, filters } = useEntityList();
  const defaultColumns: TableColumn<CatalogTableRow>[] = useMemo(() => {
    return [
      columnFactories.createTitleColumn({ hidden: true }),
      columnFactories.createNameColumn({ defaultKind: filters.kind?.value }),
      columnFactories.createEntityName(),
      ...createEntitySpecificColumns(),
      columnFactories.createTagsColumn(),
    ];
    function createEntitySpecificColumns(): TableColumn<CatalogTableRow>[] {
      const baseColumns = [
        columnFactories.createPartOfColumn(),
        columnFactories.createOwnerColumn(),
        columnFactories.createSpecTypeColumn(),
        columnFactories.createSpecLifecycleColumn(),
      ];
      switch (filters.kind?.value) {
        case 'user':
          return [
            columnFactories.createUserRoleColumn(),
            columnFactories.createUserTitleColumn(),
            columnFactories.createUserLocationColumn(),
            columnFactories.createSlackUsernameColumn(),
            columnFactories.createGitHubUsernameColumn(),
            columnFactories.createMemberOfColumn(),
          ];
        case 'domain':
          return [
            columnFactories.createPartOfColumn(),
            columnFactories.createOwnerColumn(),
          ];
        case 'system':
          return [
            columnFactories.createRepoColumn(),
            columnFactories.createPartOfColumn(),
            columnFactories.createOwnerColumn(),
          ];
        case 'group':
          return [
            columnFactories.createPartOfColumn(),
            columnFactories.createSpecTypeColumn(),
          ];
        case 'component':
        case 'resource':
          return [columnFactories.createRepoColumn(), ...baseColumns];
        case 'location':
        case 'template':
          return [columnFactories.createSpecTypeColumn()];
        default:
          return entities.every(
            entity => entity.metadata.namespace === 'default',
          )
            ? baseColumns
            : [...baseColumns, columnFactories.createNamespaceColumn()];
      }
    }
  }, [filters.kind?.value, entities]);
  const showTypeColumn = filters.type === undefined;
  const titlePreamble = capitalize(filters.user?.value ?? 'all');
  if (error) {
    return (
      <div>
        <WarningPanel
          severity="error"
          title="Could not fetch catalog entities."
        >
          <CodeSnippet language="text" text={error.toString()} />
        </WarningPanel>
      </div>
    );
  }
  const defaultActions: TableProps<CatalogTableRow>['actions'] = [
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
      const title = 'View';
      return {
        icon: () => (
          <>
            <VisuallyHidden>{title}</VisuallyHidden>
            <OpenInNew fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
      const title = 'Edit';
      return {
        icon: () => (
          <>
            <VisuallyHidden>{title}</VisuallyHidden>
            <Edit fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const isStarred = isStarredEntity(entity);
      const title = isStarred ? 'Remove from favorites' : 'Add to favorites';
      return {
        cellStyle: { paddingLeft: '1em' },
        icon: () => (
          <>
            <VisuallyHidden>{title}</VisuallyHidden>
            {isStarred ? <YellowStar /> : <StarBorder />}
          </>
        ),
        tooltip: title,
        onClick: () => toggleStarredEntity(entity),
      };
    },
  ];
  const rows = entities.sort(refCompare).map(entity => {
    let partOfRelations: CompoundEntityRef[] = [];
    let partOfRelationsDefaultKind: string | undefined = 'component';
    const e = entityUtil(entity);
    if (e.isKind(['api', 'component', 'resource', 'domain', 'system'])) {
      partOfRelationsDefaultKind = 'domain';
      partOfRelations = getEntityRelations(entity, RELATION_PART_OF);
    } else if (e.isKind('group')) {
      partOfRelationsDefaultKind = 'group';
      partOfRelations = getEntityRelations(entity, RELATION_CHILD_OF, {
        kind: 'group',
      });
    }
    const memberOfRelations = getEntityRelations(entity, RELATION_MEMBER_OF);
    const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
    return {
      entity,
      resolved: {
        name: humanizeEntityRef(entity, {
          defaultKind: 'Component',
        }),
        memberOfRelationsTitle: memberOfRelations
          .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
          .join(', '),
        memberOfRelations,
        ownedByRelationsTitle: ownedByRelations
          .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
          .join(', '),
        ownedByRelations,
        partOfRelationsTitle: partOfRelations
          .map(r => humanizeEntityRef(r))
          .join(', '),
        partOfRelations,
        partOfRelationsDefaultKind,
        userGitHubUsername: e.getAnnotation(ANNOTATION_GITHUB_USER_LOGIN),
        userLocation: e.getAnnotation(ANNOTATION_USER_LOCATION),
        userRole: e.getAnnotation(ANNOTATION_USER_ROLE),
        userTitle: e.getAnnotation(ANNOTATION_USER_TITLE),
        repo: e.getAnnotation('github.com/project-slug'),
        slack: e.getAnnotation(ANNOTATION_SLACK_NAME),
      },
    };
  });
  const typeColumn = (columns ?? defaultColumns).find(c => c.title === 'Type');
  if (typeColumn) {
    typeColumn.hidden = !showTypeColumn;
  }
  const showPagination = rows.length > 20;
  const currentKind = filters.kind?.value ?? '';
  const currentType = filters.type?.value ?? '';
  const titleDisplay = [titlePreamble, currentType, pluralize(currentKind)]
    .filter(s => s)
    .join(' ');
  return (
    <Table<CatalogTableRow>
      isLoading={loading}
      columns={columns ?? defaultColumns}
      options={{
        paging: showPagination,
        pageSize: 20,
        actionsColumnIndex: -1,
        loadingType: 'linear',
        showEmptyDataSourceMessage: !loading,
        padding: 'dense',
        pageSizeOptions: [20, 50, 100],
        ...tableOptions,
      }}
      title={`${titleDisplay} (${entities.length})`}
      data={rows}
      actions={actions ?? defaultActions}
      subtitle={subtitle}
      emptyContent={emptyContent}
    />
  );
};

CatalogTable.columns = columnFactories;
